import React, { useState } from 'react';
import { X, Download, Send, Edit, Printer } from 'lucide-react';
import { Button } from '../ui/Button';
import { StatusBadge } from '../ui/StatusBadge';
import { formatCurrency, formatDate, downloadPDF, sendEmail } from '../../lib/utils';
import { PrintableInvoice } from '../print/PrintableInvoice';
import { invoiceService } from '../../lib/database/invoices';

interface ViewInvoiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onEdit: () => void;
  invoice: any;
}

export const ViewInvoiceModal: React.FC<ViewInvoiceModalProps> = ({
  isOpen,
  onClose,
  onEdit,
  invoice,
}) => {
  const [showPrintView, setShowPrintView] = useState(false);
  const [sending, setSending] = useState(false);
  const [downloading, setDownloading] = useState(false);

  if (!isOpen || !invoice) return null;

  const handlePrint = () => {
    setShowPrintView(true);
    setTimeout(() => {
      window.print();
      setShowPrintView(false);
    }, 100);
  };

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      // In a real app, this would generate a PDF using a library
      await downloadPDF(`Invoice_${invoice.invoice_number}`, JSON.stringify(invoice));
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Failed to download PDF. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const handleSendInvoice = async () => {
    setSending(true);
    try {
      // Update invoice status to 'sent'
      await invoiceService.sendInvoice(invoice.id);
      
      // Send email notification
      if (invoice.client?.email) {
        await sendEmail(
          invoice.client.email,
          `Invoice ${invoice.invoice_number} from AI CRM`,
          `Your invoice ${invoice.invoice_number} for ${formatCurrency(Number(invoice.total))} is now available. Due date: ${formatDate(invoice.due_date)}`
        );
      }
      
      // Close modal after sending
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error) {
      console.error('Error sending invoice:', error);
      alert('Failed to send invoice. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900">Invoice Details</h2>
            <div className="flex items-center space-x-2">
              <Button variant="ghost" size="sm" icon={Edit} onClick={onEdit}>
                Edit
              </Button>
              <Button variant="ghost" size="sm" icon={Printer} onClick={handlePrint}>
                Print
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                icon={Download} 
                onClick={handleDownloadPDF}
                loading={downloading}
                disabled={downloading}
              >
                PDF
              </Button>
              {invoice.status !== 'paid' && (
                <Button 
                  variant="primary" 
                  size="sm" 
                  icon={Send} 
                  onClick={handleSendInvoice}
                  loading={sending}
                  disabled={sending}
                >
                  Send
                </Button>
              )}
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Invoice Header */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Invoice Information</h3>
                <div className="space-y-2">
                  <div>
                    <span className="text-sm text-gray-600">Invoice Number:</span>
                    <p className="font-semibold text-gray-900">{invoice.invoice_number}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600">Status:</span>
                    <div className="mt-1">
                      <StatusBadge status={invoice.status} />
                    </div>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600">Created:</span>
                    <p className="text-gray-900">{formatDate(invoice.created_at)}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600">Due Date:</span>
                    <p className="text-gray-900">{formatDate(invoice.due_date)}</p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Client Information</h3>
                <div className="space-y-2">
                  <div>
                    <span className="text-sm text-gray-600">Name:</span>
                    <p className="font-semibold text-gray-900">{invoice.client?.name || 'Unknown'}</p>
                  </div>
                  {invoice.client?.company && (
                    <div>
                      <span className="text-sm text-gray-600">Company:</span>
                      <p className="text-gray-900">{invoice.client.company}</p>
                    </div>
                  )}
                  <div>
                    <span className="text-sm text-gray-600">Email:</span>
                    <p className="text-gray-900">{invoice.client?.email || 'No email'}</p>
                  </div>
                  {invoice.client?.phone && (
                    <div>
                      <span className="text-sm text-gray-600">Phone:</span>
                      <p className="text-gray-900">{invoice.client.phone}</p>
                    </div>
                  )}
                  {invoice.client?.address && (
                    <div>
                      <span className="text-sm text-gray-600">Address:</span>
                      <p className="text-gray-900">{invoice.client.address}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Invoice Items */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Invoice Items</h3>
              <div className="overflow-x-auto">
                <table className="w-full border border-gray-200 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left py-3 px-4 font-semibold text-gray-900">Description</th>
                      <th className="text-right py-3 px-4 font-semibold text-gray-900">Qty</th>
                      <th className="text-right py-3 px-4 font-semibold text-gray-900">Rate</th>
                      <th className="text-right py-3 px-4 font-semibold text-gray-900">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoice.invoice_items?.map((item: any, index: number) => (
                      <tr key={item.id || index} className="border-t border-gray-200">
                        <td className="py-3 px-4 text-gray-900">{item.description}</td>
                        <td className="py-3 px-4 text-right text-gray-900">{Number(item.quantity).toFixed(2)}</td>
                        <td className="py-3 px-4 text-right text-gray-900">{formatCurrency(Number(item.rate))}</td>
                        <td className="py-3 px-4 text-right font-semibold text-gray-900">{formatCurrency(Number(item.amount))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Totals */}
            <div className="bg-gray-50 p-6 rounded-lg">
              <div className="space-y-2 max-w-sm ml-auto">
                <div className="flex justify-between">
                  <span className="text-gray-600">Subtotal:</span>
                  <span className="font-medium">{formatCurrency(Number(invoice.subtotal))}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Tax ({Number(invoice.tax_rate)}%):</span>
                  <span className="font-medium">{formatCurrency(Number(invoice.tax_amount))}</span>
                </div>
                <div className="flex justify-between text-lg font-bold border-t pt-2">
                  <span>Total:</span>
                  <span>{formatCurrency(Number(invoice.total))}</span>
                </div>
              </div>
            </div>

            {/* Notes */}
            {invoice.notes && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Notes</h3>
                <p className="text-gray-700 bg-gray-50 p-4 rounded-lg">{invoice.notes}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Printable Version */}
      <PrintableInvoice invoice={invoice} isVisible={showPrintView} />
    </>
  );
};